import {DateTime} from 'luxon'

import {TranslatorScope} from '../../locale/types'
import {theme} from '../../theme/theme'

export const durationTypesLocalesMap = {
  DAYS: 'days',
  WEEK: 'weeks',
  MONTH: 'months',
} as const

export type BookingLike = {
  expireDate?: number
  endDate?: number
  subStatus?: 'AWAITING_PAYMENT' | 'CANCELLED' | 'EXPIRED' | 'FROZEN' | (string & {})
  status?: 'ACTIVE' | 'INACTIVE' | (string & {})
  durationType?: keyof typeof durationTypesLocalesMap
  duration?: number
  usageStatuses?: string[]
}

export const buildChips = (
  subscriptionBooking: BookingLike | null | undefined,
  l: (key: TranslatorScope, params?: Record<string, unknown>) => string,
) => {
  const result: {text: string; color: string; textColor: string}[] = []
  if (!subscriptionBooking) {
    return result
  }
  const now = DateTime.now()
  const expireDate = DateTime.fromMillis(subscriptionBooking.expireDate || subscriptionBooking.endDate || 0)
  const daysToExpire = expireDate.diff(now).as('days')
  if (daysToExpire > 0 && daysToExpire < 7) {
    result.push({text: l('expiringSoon', {days: daysToExpire}), color: theme.colors['bg-expiring'], textColor: theme.colors['text-contrast']})
  }
  if (subscriptionBooking.subStatus === 'AWAITING_PAYMENT') {
    result.push({text: l('awaitingPayment'), color: theme.colors.notify, textColor: theme.colors['text-contrast']})
  }
  result.push(
    subscriptionBooking.status === 'ACTIVE'
      ? {text: l('active'), color: theme.colors.success, textColor: theme.colors['text-contrast']}
      : {text: l('inactive'), color: theme.colors.error, textColor: theme.colors['text-contrast']},
  )
  if (subscriptionBooking.subStatus === 'FROZEN') {
    result.push({text: l('frozen'), color: theme.colors['bg-frozen'], textColor: theme.colors['text-contrast']})
  }
  if (subscriptionBooking.subStatus === 'CANCELLED') {
    result.push({text: l('cancelled'), color: theme.colors.error, textColor: theme.colors['text-contrast']})
  }
  if (subscriptionBooking.subStatus === 'EXPIRED') {
    result.push({text: l('expired'), color: theme.colors.error, textColor: theme.colors['text-contrast']})
  }
  if (subscriptionBooking.durationType && subscriptionBooking.duration) {
    const key = durationTypesLocalesMap[subscriptionBooking.durationType]
    result.push({
      textColor: theme.colors['text-contrast'],
      color: 'rgba(0, 16, 61, 0.48)',
      text: l(`${key}_single` as TranslatorScope, {
        value: subscriptionBooking.duration,
        declension: [`${key}_single_genitive`, `${key}_plural`] as [TranslatorScope, TranslatorScope],
      }),
    })
  }
  if (Array.isArray(subscriptionBooking.usageStatuses) && subscriptionBooking.usageStatuses.length) {
    if (subscriptionBooking.usageStatuses.includes('RUN_OUT')) {
      result.push({text: l('runOut'), color: theme.colors['bg-info-tag'], textColor: theme.colors.error})
    } else {
      result.push({text: l('runningOut'), color: theme.colors['bg-expiring'], textColor: theme.colors['text-contrast']})
    }
  }
  return result
}

export type SubscriptionBookingAccess = 'notOwner' | 'owner' | 'pending'

/**
 * Whether the signed-in user may see this held pass. The Subscriptions list returns the venue's passes whose
 * memberId is the user's contact OR whose userId is the user (POST bookings/:venueId/memberSubscriptions/:memberId),
 * so the profile accepts both: a pass allocated to the contact before they had an account keeps userId null.
 * 'pending' while the booking, the user or the contact is still loading, so we never redirect on a half-loaded state.
 * A failed contact lookup is no verdict either: the pass stays on screen rather than a silent jump to the venue home.
 */
export const getSubscriptionBookingAccess = ({
  booking,
  userId,
  memberId,
  isMemberLoading,
  isMemberError = false,
}: {
  booking?: {userId?: string | null; memberId?: string | null} | null
  userId?: string
  memberId?: string
  isMemberLoading: boolean
  isMemberError?: boolean
}): SubscriptionBookingAccess => {
  if (!booking || !userId) {
    return 'pending'
  }
  if (booking.userId && booking.userId === userId) {
    return 'owner'
  }
  if (isMemberLoading) {
    return 'pending'
  }
  if (booking.memberId && memberId && booking.memberId === memberId) {
    return 'owner'
  }
  if (isMemberError && !memberId) {
    return 'pending'
  }
  return 'notOwner'
}

